/// <amd-module name="Wit/WorkItemForm/History/UpdateSummary" />
import * as React from "react";
import { useMemo } from "react";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import * as Resources from "Wit/WorkItemForm/Resources";
import { WorkItemFieldUpdate } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { stripIdPartFromDisplayName } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IHistoryItem } from "../Common/Services";
import { useFieldMetadata, useWorkItem } from "../Common/Hooks";
import { FieldValue } from "../Common/Components";
import { getAuthorizedAsIdentity, hasAttachmentChangesOfType, hasLinkChangesOfType, hasLinkUpdates, hasSameNameWithRevisedBy, isCalculatedField, isLinkUpdate } from "./HistoryUtils";
import { ChangeType } from "./ChangeType";
import { IFieldLookup } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { getLinkTypeDisplayName } from "Wit/WorkItemLinksRegistration/LinkUtils";
import { usePageContext } from "VSS/Platform/Layout";

export interface IUpdateSummaryProps {
    className?: string;
    update: IHistoryItem;
}

export function UpdateSummary({ update, className }: IUpdateSummaryProps) {
    const workItem = useWorkItem();
    const fieldLookup = useFieldMetadata();
    const pageContext = usePageContext();
    const changes = useMemo(() => getChanges(update, fieldLookup, workItem), [update, workItem]);

    const authorizedAsIdentity = getAuthorizedAsIdentity(update, pageContext);
    const haveSameName = hasSameNameWithRevisedBy(update, authorizedAsIdentity);

    const displayName = stripIdPartFromDisplayName(update.revisedBy.displayName);
    const author = authorizedAsIdentity && !haveSameName ? 
        format(Resources.HistoryControlViaTemplate, displayName, authorizedAsIdentity.displayName)
        : displayName;

    const content = useMemo(() => {
        switch (changes.length) {
            case 0:
                return "TODO: how did this happen?";
            case 1:
                return <FormatComponent format={Resources.Made1Change}>{[author, ...changes]}</FormatComponent>;
            case 2:
                return <FormatComponent format={Resources.Made2Changes}>{[author, ...changes]}</FormatComponent>;
            case 3:
                return <FormatComponent format={Resources.Made3Changes}>{[author, ...changes]}</FormatComponent>;
            default:
                return <FormatComponent format={Resources.MadeMoreChanges}>{[author, ...changes]}</FormatComponent>;
        }
    }, [update]);

    return <span className={className}>{content}</span>;
}

interface IChangeSummary {
    description: JSX.Element | string;
    order: number;
}

export function hasChanges(update: IHistoryItem, fieldLookup: IFieldLookup, workItem: IWorkItem): boolean {
    return getChanges(update, fieldLookup, workItem).length > 0;
}

function getChanges(update: IHistoryItem, fieldLookup: IFieldLookup, workItem: IWorkItem): (JSX.Element | string)[] {
    const changes: IChangeSummary[] = [];

    if (update.rev === 1 && !hasLinkUpdates(update)) {
        changes.push({
            order: 0,
            description: (
                <FormatComponent format={Resources.CreatedTheWorkItem} key={`work-item-created-${update.id}`}>
                    {[workItem.getFieldValueByName(CoreFieldRefNames.WorkItemType)]}
                </FormatComponent>
            )
        });
    } else if (hasFieldChanges(update, fieldLookup)) {
        const fieldChanges: IChangeSummary[] = [];

        for (const [refName, fieldUpdate] of Object.entries(update.fields)) {
            if (isCalculatedField(refName, fieldLookup) || refName === CoreFieldRefNames.History) {
                // Do not include these fields in field changes
                continue;
            }

            const fieldName = fieldLookup[refName]?.name ?? refName;
            const key = `${refName}-update-${update.id}`;

            if (refName === CoreFieldRefNames.IsDeleted) {
                changes.push({
                    order: 1,
                    description: fieldUpdate.newValue ? Resources.DeletedTheWorkItem : Resources.RestoredTheWorkItem
                });
                continue;
            }

            if (refName === CoreFieldRefNames.AssignedTo && (fieldUpdate.newValue || fieldUpdate.oldValue)) {
                changes.push({
                    order: 1,
                    description: fieldUpdate.newValue ? (
                        <FormatComponent format={Resources.AssignedPerson} key={key}>
                            <FieldValue refName={refName} fieldValue={fieldUpdate.newValue} />
                        </FormatComponent>
                    ) : (
                        <FormatComponent format={Resources.UnassignedPerson} key={key}>
                            <FieldValue refName={refName} fieldValue={fieldUpdate.oldValue} />
                        </FormatComponent>
                    )
                });
                continue;
            }

            if (refName === CoreFieldRefNames.State) {
                changes.push({
                    order: 1,
                    description: (
                        <FormatComponent format={Resources.ChangedFieldFromOldToNew} key={key}>
                            {fieldName}
                            <FieldValue refName={refName} fieldValue={fieldUpdate.oldValue} />
                            <FieldValue refName={refName} fieldValue={fieldUpdate.newValue} />
                        </FormatComponent>
                    )
                });
                continue;
            }

            const fieldEntry = fieldLookup[refName];
            if (fieldEntry?.type === FieldType.Html || fieldEntry?.type === FieldType.PlainText || refName === CoreFieldRefNames.Tags) {
                fieldChanges.push({
                    order: 1,
                    description: format(Resources.ChangedField, fieldName)
                });
                continue;
            }

            if (shouldShowOldValueInSummary(refName, fieldUpdate)) {
                fieldChanges.push({
                    order: 1,
                    description: (
                        <FormatComponent format={Resources.ChangedFieldFromOldToNew} key={key}>
                            {fieldName}
                            <FieldValue refName={refName} fieldValue={fieldUpdate.oldValue} />
                            <FieldValue refName={refName} fieldValue={fieldUpdate.newValue} />
                        </FormatComponent>
                    )
                });
                continue;
            }

            fieldChanges.push({
                order: 1,
                description:
                    fieldUpdate.newValue !== undefined ? (
                        <FormatComponent format={Resources.ChangedFieldToNew} key={key}>
                            {fieldName}
                            <FieldValue refName={refName} fieldValue={fieldUpdate.newValue} />
                        </FormatComponent>
                    ) : (
                        <FormatComponent format={Resources.ChangedField} key={key}>
                            {fieldName}
                        </FormatComponent>
                    )
            });
        }

        if (fieldChanges.length === 1) {
            changes.push(fieldChanges[0]);
        } else {
            changes.push({
                order: 1,
                description: Resources.MadeFieldChanges
            });
        }
    }

    if (hasAttachmentChangesOfType(update, ChangeType.Added)) {
        changes.push({
            order: 2,
            description: Resources.AddedAnAttachment
        });
    }

    if (hasAttachmentChangesOfType(update, ChangeType.Removed)) {
        changes.push({
            order: 2,
            description: Resources.DeletedAnAttachment
        });
    }

    if (hasLinkChangesOfType(update, ChangeType.Added)) {
        const linkType = getLinkTypeDisplayName(update.relations?.added.filter(isLinkUpdate)[0]?.attributes?.name);
        changes.push({
            order: 3,
            description: linkType ? format(Resources.AddedALinkOfType, linkType) : Resources.AddedALink
        });
    }

    if (hasLinkChangesOfType(update, ChangeType.Removed)) {
        const linkType = getLinkTypeDisplayName(update.relations?.removed.filter(isLinkUpdate)[0]?.attributes?.name);
        changes.push({
            order: 3,
            description: linkType ? format(Resources.DeletedALinkOfType, linkType) : Resources.DeletedALink
        });
    }

    if (hasCommentChangeOfType(update, ChangeType.Added)) {
        changes.push({
            order: 3,
            description: Resources.AddedAComment
        });
    }

    if (hasCommentChangeOfType(update, ChangeType.Updated)) {
        changes.push({
            order: 3,
            description: Resources.EditedAComment
        });
    }

    if (hasCommentChangeOfType(update, ChangeType.Removed)) {
        changes.push({
            order: 3,
            description: Resources.DeletedAComment
        });
    }

    changes.sort((a, b) => a.order - b.order);

    return changes.map(c => c.description);
}

function shouldShowOldValueInSummary(refName: string, fieldUpdate: WorkItemFieldUpdate) {
    return !!fieldUpdate.oldValue && [CoreFieldRefNames.State, CoreFieldRefNames.TeamProject, CoreFieldRefNames.WorkItemType].includes(refName);
}

function hasFieldChanges(update: IHistoryItem, fieldLookup: IFieldLookup) {
    return (
        Object.keys(update.fields ?? {}).filter(refName => !isCalculatedField(refName, fieldLookup) && refName !== CoreFieldRefNames.History).length >
        0
    );
}

function hasCommentChangeOfType(update: IHistoryItem, changeType: ChangeType) {
    if (!update.commentEdit) {
        return false;
    }

    switch (changeType) {
        case ChangeType.Added:
            return update.commentEdit.text && !update.commentEdit.previousText;
        case ChangeType.Updated:
            return update.commentEdit.text && update.commentEdit.previousText;
        case ChangeType.Removed:
            return !update.commentEdit.text && update.commentEdit.previousText;
    }
}
